<template>
  <div>
    <div>
      <div class="survey-table">
        <BaseModal v-model="wipeconfirm" hide-footer title="Delete survey permanently?">
          <p v-if="wipesurvey">
            WARNING! This operation will physically delete ALL information for this survey,
            including survey participants, answers to survey questions, and all reports.
          </p>
          <div v-if="wipesurveys.length > 0">
            WARNING! This operation will physically delete ALL information for the selected surveys,
            including survey participants, answers to survey questions, and all reports.
            <br />
            <br />
            This can take a long time depending on the amount of surveys selected. Please do not close the browser
            window.
          <ul class="mt-3">
            <li v-for="survey in wipesurveys" :key="survey.id" class="mb-1">{{ survey.name }} ({{ survey.id }})</li>
          </ul>
          </div>
          <p><strong>THIS OPERATION CANNOT BE REVERSED.</strong></p>
          <button class="btn btn-danger btn-block mt-3" @click="survey_deletefromdatabase(wipesurvey)">
            Delete
          </button>
          <button class="btn btn-secondary btn-block mt-2" @click="wipeconfirm = false; wipesurvey = false;">
            Cancel
          </button>
        </BaseModal>
        <BaseModal v-model="wipeprogress" hide-footer title="Permanently deleting survey(s)">
          <div class="row">
            <div class="col-12 text-center">
              <h5>Deleting survey(s)</h5>
            </div>
          </div>
          <div class="col-12 text-center">
            <div class="spinner-border text-danger" role="status" style="width: 4rem; height: 4rem;">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </BaseModal>
        <BaseModal v-model="deleteconfirm" hide-footer>
          D ya?
        </BaseModal>

        <div class="table-filter-area">
          <div class="row">
            <div class="col-md-8 pt-4">
              <div class="form-group">
                <input type="text" v-model="search" @input="handleSearch" class="form-control" placeholder="Search" />
              </div>
            </div>
            <div class="col-md-4 pt-4 text-right">
              <router-link :to="{ name: 'creategroupsurvey' }" class="btn btn-success">
                Create new survey
              </router-link>
            </div>
          </div>
          <div class="table-button-area mb-3">
            <div class="d-flex justify-content-between">
              <div>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 3, 'btn-secondary': survey_status_id !== 3 }"
                  @click="filter_table(3)">
                  Ongoing ({{ surveycount_3 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 1, 'btn-secondary': survey_status_id !== 1 }"
                  @click="filter_table(1)">
                  Drafts ({{ surveycount_1 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 2, 'btn-secondary': survey_status_id !== 2 }"
                  @click="filter_table(2)">
                  Scheduled ({{ surveycount_2 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 4, 'btn-secondary': survey_status_id !== 4 }"
                  @click="filter_table(4)">
                  Completed ({{ surveycount_4 }})
                </button>
                <button class="btn btn-small"
                  :class="{ 'btn-primary': survey_status_id === 5, 'btn-secondary': survey_status_id !== 5 }"
                  @click="filter_table(5)">
                  Trash ({{ surveycount_5 }})
                </button>
              </div>
            </div>
          </div>
        </div>

        <BaseTable
          id="surveys"
          ref="surveysTable"
          :key="'survey-table-' + survey_status_id.value + '-' + search.value"
          hover
          :items="filteredSurveys"
          :fields="fields"
          :busy="loading"
          :sort-by="orderBy"
          :sort-desc="orderDesc"
          :no-local-sorting="true"
          @sort-changed="surveys_sort"
        >
          <template v-slot:cell(survey_name)="data">
            <div class="d-flex align-items-center">
              <div>
                {{ data.item.survey_name }}
              </div>
              <div v-if="data.item.is_template" class="ml-2 badge badge-info">
                Template
              </div>
            </div>
          </template>
          <template v-slot:cell(survey_language_id)="data">
            {{ data.item.survey_language_id }}
          </template>
          <template v-slot:cell(survey_start_date)="data">
            {{ formatDate(data.item.survey_start_date) }}
          </template>
          <template v-slot:cell(survey_end_date)="data">
            {{ formatDate(data.item.survey_end_date) }}
          </template>
          <template v-slot:cell(actions)="data">
            <div class="d-flex justify-content-end">
              <BaseDropdown right class="ml-2">
                <template #button-content>
                  <i class="fa-solid fa-ellipsis"></i>
                </template>
                <a class="dropdown-item" @click.prevent="editSurvey(data.item)">Edit</a>
                <a v-if="survey_status_id !== 5" class="dropdown-item"
                  @click.prevent="survey_delete(data.item)">Trash</a>
                <a v-if="survey_status_id === 5" class="dropdown-item"
                  @click.prevent="survey_delete(data.item)">Restore</a>
                <a class="dropdown-item" @click.prevent="deleteSurvey(data.item)">Delete</a>
              </BaseDropdown>
            </div>
          </template>
        </BaseTable>

        <div class="row">
          <div class="col-md-12 text-center mt-3">
            <div>Showing {{ (currentPage - 1) * perPage + 1 }} to {{ Math.min(currentPage * perPage, totalSurveys) }} of {{ totalSurveys }}</div>
            <nav aria-label="Survey pagination">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                  <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                </li>
                <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: currentPage === page }">
                  <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                  <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import BaseTable from './base/BaseTable.vue'
import BaseDropdown from './base/BaseDropdown.vue'
import BaseModal from './base/BaseModal.vue'

const props = defineProps({
  groupid: {
    type: [Number, String],
    required: true
  },
  surveytype: {
    type: String,
    default: ''
  }
})

const store = useStore()
const router = useRouter()

// Reactive data
const surveys = ref([])
const loading = ref(false)
const currentPage = ref(1)
const perPage = ref(20)
const totalSurveys = ref(0)
const orderBy = ref('survey_start_date')
const orderDesc = ref(true)
const search = ref('')
const survey_status_id = ref(3)
const surveycount_1 = ref(0)

// Filtered surveys computed includes both API filter and client search
const filteredSurveys = computed(() => {
  let result = surveys.value
  
  if (result.length > 0) {
    if (survey_status_id.value === 5) {
      // Trash tab: filter by survey_deleted flag (DB has no status 5)
      result = result.filter(survey => Number(survey.survey_deleted) === 1)
    } else if (survey_status_id.value) {
      // Active status tabs: filter by status AND exclude deleted items
      result = result.filter(survey =>
        Number(survey.survey_status_id) === survey_status_id.value &&
        Number(survey.survey_deleted) === 0
      )
    }
  }
  
  // Filter by search if present
  if (!search.value) return result
  return result.filter(survey => 
    survey.survey_name.toLowerCase().includes(search.value.toLowerCase()) ||
    (survey.language_name && survey.language_name.toLowerCase().includes(search.value.toLowerCase()))
  )
})
const surveycount_2 = ref(0)
const surveycount_3 = ref(0)
const surveycount_4 = ref(0)
const surveycount_5 = ref(0)
const wipeconfirm = ref(false)
const wipesurvey = ref(false)
const wipesurveys = ref([])
const wipeprogress = ref(false)
const deleteconfirm = ref(false)

const fields = [
  { key: 'survey_name', label: 'Survey name', sortable: true },
  { key: 'survey_language_id', label: 'Language', sortable: false },
  { key: 'survey_start_date', label: 'Start date', sortable: true },
  { key: 'survey_end_date', label: 'End date', sortable: true },
  { key: 'actions', label: 'Actions', class: 'ellipsis-button' }
]

// Computed properties
const totalPages = computed(() => {
  return Math.ceil(totalSurveys.value / perPage.value)
})

// Methods
const filter_table = (status_id) => {
  survey_status_id.value = status_id
  currentPage.value = 1
  getSurveys()
}

const getLanguageName = (languageId) => {
  const lang = store.state.languages?.find(l => Number(l.id) === Number(languageId))
  return lang ? lang.language_name : ''
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}


const editSurvey = (survey) => {
  store.dispatch('groupsurvey_setcurrent', { survey_id: survey.id }).then(() => {
    if (Number(survey.survey_status_id) === 1) {
      router.push('/create/group/survey/details')
    } else {
      router.push('/group/survey')
    }
  })
}

const survey_delete = (survey) => {
  // Soft delete - move to trash
  store.dispatch('groupsurvey_update', { 
    survey: { 
      id: survey.id, 
      survey_deleted: 1 - survey.survey_deleted 
    } 
  }).then(() => {
    getSurveys()
  })
}

const deleteSurvey = (survey) => {
  wipesurvey.value = survey.id
  wipesurveys.value = [survey]
  wipeconfirm.value = true
}

const activeStatus = computed({
  get: () => survey_status_id.value,
  set: (val) => { survey_status_id.value = val; currentPage.value = 1; getSurveys() }
})

const surveycounts_get = async () => {
  const filter = { 
    group_id: { operator: '=', value: props.groupid },
    survey_deleted: { operator: '=', value: '0', realfield: 1 } 
  }
  
  surveycount_1.value = await store.dispatch('surveys_fetch', {
    filter: { ...filter, survey_status_id: { operator: '=', value: '1', realfield: 1 } },
    countonly: 1
  }).then(r => r.data.total)
  
  surveycount_2.value = await store.dispatch('surveys_fetch', {
    filter: { ...filter, survey_status_id: { operator: '=', value: '2', realfield: 1 } },
    countonly: 1
  }).then(r => r.data.total)
  
  surveycount_3.value = await store.dispatch('surveys_fetch', {
    filter: { ...filter, survey_status_id: { operator: '=', value: '3', realfield: 1 } },
    countonly: 1
  }).then(r => r.data.total)
  
  surveycount_4.value = await store.dispatch('surveys_fetch', {
    filter: { ...filter, survey_status_id: { operator: '=', value: '4', realfield: 1 } },
    countonly: 1
  }).then(r => r.data.total)
  
  surveycount_5.value = await store.dispatch('surveys_fetch', {
    filter: { survey_deleted: { operator: '=', value: '1', realfield: 1 }, group_id: { operator: '=', value: props.groupid } },
    countonly: 1
  }).then(r => r.data.total)
}

const handleSearch = () => {
  // Search will be handled by computed filter
}

const surveys_sort = ({ sortBy, sortDesc } = {}) => {
  if (sortBy) {
    orderBy.value = sortBy
  }
  if (typeof sortDesc === 'boolean') {
    orderDesc.value = sortDesc
  }
  getSurveys()
}

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  getSurveys()
}

const getSurveys = async () => {
  loading.value = true
  try {
    const filter = {}

    // Build filter - always include status and deleted filter
    filter.survey_status_id = { operator: '=', value: survey_status_id.value, realfield: 1 }
    filter.survey_deleted = { operator: '=', value: survey_status_id.value === 5 ? '1' : '0', realfield: 1 }

    if (search.value) {
      filter.survey_name = {
        operator: 'LIKE',
        value: `%${search.value}%`
      }
    }

    // Adjusted for Vuex store usage and pagination
    await store.dispatch('groupsurveys_fetch', {
      group_id: props.groupid,
      filter,
      orderBy: orderBy.value,
      orderDesc: orderDesc.value,
      start: (currentPage.value - 1) * perPage.value,
      limit: perPage.value
    })
      .then((resp) => {
        surveys.value = Array.isArray(resp.data.result) ? resp.data.result : []
        totalSurveys.value = resp.data.total 
        loading.value = false
      })
  } catch (error) {
    console.error('Error fetching surveys:', error)
    loading.value = false
  }
}

const survey_deletefromdatabase = async (surveyToDelete) => {
  wipeconfirm.value = false
  wipeprogress.value = true
  
  if (wipesurvey.value) {
    await store.dispatch('groupsurvey_wipe', { id: wipesurvey.value })
    wipesurvey.value = 0
  } else if (wipesurveys.value.length > 0) {
    for (let i = 0; i < wipesurveys.value.length; i++) {
      await store.dispatch('groupsurvey_wipe', { id: surveys.value[i].id })
    }
    wipesurveys.value = []
  }
  
  getSurveys()
  wipeprogress.value = false
}

// Lifecycle
onMounted(() => {
  store.dispatch('languages_fetch')
  getSurveys()
  surveycounts_get()
})

// Watchers
watch(search, () => {
  currentPage.value = 1
  getSurveys()
})
</script>

<style scoped>
.survey-table {
  position: relative;
}
</style>